class ImageHotspotSplit extends HTMLElement {
  constructor () {
    super()
    this.outer = this.querySelector('.image-hotspot-split-outer')
    this.galleryContainer = this.querySelector('.hotspot-split-gallery-container')
    this.galleryTrack = this.querySelector('[data-gallery-track]')
    this.popupContainer = this.querySelector('[data-popup-container]')
    this.slides = this.querySelectorAll('.hotspot-split-slide')
    this.productsGrid = this.querySelector('[data-products-grid]')
    this.mobileScrollContainer = this.querySelector('[data-scroll-container]')
    this.scrollIndicator = this.querySelector('.hotspot-scroll-indicator')
    this.isMobile = window.innerWidth <= 780
    this.activeSlideIndex = 1
    this.slideCount = this.slides.length
    this.hideTimeout = null
    this.activeDot = null
    this.mobileVisualIndex = 0
    this.mobileSnapItems = []
  }

  connectedCallback () {
    this.computeMobileSnapItems()
    this.initGallery()
    this.initHotspots()
    this.initMobileScroll()
    this.initResizeHandler()
    this.updateVisibleProducts()
  }

  computeMobileSnapItems () {
    const items = []
    this.slides.forEach(slide => {
      const slideIndex = parseInt(slide.dataset.slideIndex)
      if (slide.classList.contains('split-layout-full')) {
        items.push(slideIndex)
      } else {
        const panels = slide.querySelectorAll('.image-hotspot-split-panel')
        panels.forEach(() => {
          items.push(slideIndex)
        })
      }
    })
    this.mobileSnapItems = items
  }

  initGallery () {
    if (!this.galleryTrack) return

    // Scroll detection (always needed — mobile split slides create multiple snap items)
    let scrollTimeout
    this.galleryTrack.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        this.detectCurrentSlide()
      }, 100)
    })

    if (this.slideCount <= 1) return

    // Click navigation - left half goes prev, right half goes next
    this.galleryTrack.addEventListener('click', (e) => {
      if (e.target.closest('.hotspot-dot') ||
          e.target.closest('.hotspot-popup') ||
          e.target.closest('.hotspot-popup-container') ||
          e.target.closest('.hotspot-products-grid') ||
          e.target.closest('a') ||
          e.target.closest('button')) {
        return
      }

      if (this.outer && this.outer.classList.contains('split-view-active')) {
        return
      }

      const rect = this.galleryTrack.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const halfWidth = rect.width / 2

      if (clickX < halfWidth) {
        this.goToSlide('prev')
      } else {
        this.goToSlide('next')
      }
    })

    // Update cursor based on mouse position
    this.galleryTrack.addEventListener('mousemove', (e) => {
      if (e.target.closest('.hotspot-dot') ||
          e.target.closest('a') ||
          e.target.closest('button')) {
        this.galleryTrack.classList.remove('cursor-left', 'cursor-right')
        return
      }

      const rect = this.galleryTrack.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const halfWidth = rect.width / 2

      if (mouseX < halfWidth) {
        this.galleryTrack.classList.add('cursor-left')
        this.galleryTrack.classList.remove('cursor-right')
      } else {
        this.galleryTrack.classList.add('cursor-right')
        this.galleryTrack.classList.remove('cursor-left')
      }
    })

    this.galleryTrack.addEventListener('mouseleave', () => {
      this.galleryTrack.classList.remove('cursor-left', 'cursor-right')
    })
  }

  detectCurrentSlide () {
    if (!this.galleryTrack) return

    const scrollLeft = this.galleryTrack.scrollLeft
    const viewportWidth = this.galleryTrack.offsetWidth

    if (this.isMobile && this.mobileSnapItems.length > 0) {
      const visualIndex = Math.round(scrollLeft / viewportWidth)
      this.mobileVisualIndex = visualIndex
      const newIndex = this.mobileSnapItems[visualIndex]
      if (newIndex && newIndex !== this.activeSlideIndex) {
        this.activeSlideIndex = newIndex
        this.updateVisibleProducts()
      }
      return
    }

    const newIndex = Math.round(scrollLeft / viewportWidth) + 1

    if (newIndex !== this.activeSlideIndex && newIndex >= 1 && newIndex <= this.slideCount) {
      this.activeSlideIndex = newIndex
      this.updateVisibleProducts()
      this.hidePopup()

      if (this.outer && this.outer.classList.contains('split-view-active')) {
        this.closeSplitView()
      }
    }
  }

  goToSlide (direction) {
    if (!this.galleryTrack) return

    const viewportWidth = this.galleryTrack.offsetWidth

    if (this.isMobile && this.mobileSnapItems.length > 0) {
      const totalVisual = this.mobileSnapItems.length
      let targetVisual = this.mobileVisualIndex
      if (direction === 'prev') {
        targetVisual = targetVisual <= 0 ? totalVisual - 1 : targetVisual - 1
      } else {
        targetVisual = targetVisual >= totalVisual - 1 ? 0 : targetVisual + 1
      }
      this.galleryTrack.scrollTo({
        left: targetVisual * viewportWidth,
        behavior: 'smooth'
      })
      return
    }

    let targetIndex = this.activeSlideIndex

    if (direction === 'prev') {
      targetIndex = this.activeSlideIndex <= 1 ? this.slideCount : this.activeSlideIndex - 1
    } else {
      targetIndex = this.activeSlideIndex >= this.slideCount ? 1 : this.activeSlideIndex + 1
    }

    this.galleryTrack.scrollTo({
      left: (targetIndex - 1) * viewportWidth,
      behavior: 'smooth'
    })
  }

  initHotspots () {
    if (this.isMobile) return

    // Dot hover - show popup
    this.addEventListener('mouseenter', (e) => {
      const dot = e.target.closest('.hotspot-dot')
      if (dot) {
        this.clearHideTimeout()
        this.showPopup(dot)
      }
    }, true)

    // Dot/popup leave - schedule hide
    this.addEventListener('mouseleave', (e) => {
      const dot = e.target.closest('.hotspot-dot')
      const popup = e.target.closest('.hotspot-popup-container')

      if (dot || popup) {
        const relatedTarget = e.relatedTarget
        const isMovingToPopup = relatedTarget?.closest('.hotspot-popup-container')
        const isMovingToDot = relatedTarget?.closest('.hotspot-dot')

        if (!isMovingToPopup && !isMovingToDot) {
          this.scheduleHidePopup()
        }
      }
    }, true)

    // Popup container hover - keep visible
    if (this.popupContainer) {
      this.popupContainer.addEventListener('mouseenter', () => {
        this.clearHideTimeout()
      })
      this.popupContainer.addEventListener('mouseleave', (e) => {
        const isMovingToDot = e.relatedTarget?.closest('.hotspot-dot')
        if (!isMovingToDot) {
          this.scheduleHidePopup()
        }
      })
    }

    // Click handler for dots - open split view
    this.addEventListener('click', (e) => {
      const dot = e.target.closest('.hotspot-dot')
      if (dot && !e.target.closest('.hotspot-popup-inner')) {
        e.preventDefault()
        e.stopPropagation()
        this.hidePopup()
        this.closeOtherSplitViews()
        this.toggleSplitView()
      }
    })

    // Close split view when clicking on gallery
    if (this.galleryContainer) {
      this.galleryContainer.addEventListener('click', (e) => {
        if (this.outer && this.outer.classList.contains('split-view-active')) {
          if (!e.target.closest('.hotspot-dot') && !e.target.closest('.hotspot-popup-container')) {
            e.preventDefault()
            e.stopPropagation()
            this.closeSplitView()
          }
        }
      })
    }
  }

  showPopup (dot) {
    if (!this.popupContainer) return

    const popup = dot.querySelector('.hotspot-popup')
    if (!popup) return

    this.activeDot = dot
    dot.classList.add('active')

    this.popupContainer.innerHTML = popup.innerHTML
    this.popupContainer.classList.add('visible')

    this.positionPopup(dot)
  }

  hidePopup () {
    if (!this.popupContainer) return

    this.popupContainer.classList.remove('visible')

    setTimeout(() => {
      if (!this.popupContainer.classList.contains('visible')) {
        this.popupContainer.innerHTML = ''
      }
    }, 200)

    if (this.activeDot) {
      this.activeDot.classList.remove('active')
      this.activeDot = null
    }
  }

  scheduleHidePopup () {
    this.hideTimeout = setTimeout(() => {
      this.hidePopup()
    }, 200)
  }

  clearHideTimeout () {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
  }

  positionPopup (dot) {
    if (!this.popupContainer || !this.galleryContainer) return

    const dotRect = dot.getBoundingClientRect()
    const containerRect = this.galleryContainer.getBoundingClientRect()

    const dotCenterX = dotRect.left + dotRect.width / 2 - containerRect.left
    const dotBottom = dotRect.bottom - containerRect.top

    this.popupContainer.classList.remove('position-left', 'position-right', 'position-above')

    this.popupContainer.style.left = dotCenterX + 'px'
    this.popupContainer.style.top = dotBottom + 10 + 'px'
    this.popupContainer.style.bottom = 'auto'

    requestAnimationFrame(() => {
      const popupRect = this.popupContainer.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      if (popupRect.right > viewportWidth - 20) {
        this.popupContainer.classList.add('position-left')
      } else if (popupRect.left < 20) {
        this.popupContainer.classList.add('position-right')
      }

      if (popupRect.bottom > viewportHeight - 20) {
        this.popupContainer.classList.add('position-above')
        this.popupContainer.style.top = 'auto'
        this.popupContainer.style.bottom = (containerRect.bottom - dotRect.top + 10) + 'px'
      }
    })
  }

  initMobileScroll () {
    if (!this.mobileScrollContainer || !this.scrollIndicator) return

    this.mobileScrollContainer.addEventListener('scroll', () => {
      if (this.mobileScrollContainer.scrollLeft > 10) {
        this.scrollIndicator.classList.add('hidden')
      } else {
        this.scrollIndicator.classList.remove('hidden')
      }
    })
  }

  initResizeHandler () {
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile
      this.isMobile = window.innerWidth <= 780
      this.hidePopup()

      if (this.isMobile && this.outer && this.outer.classList.contains('split-view-active')) {
        this.outer.classList.remove('split-view-active')
        this.outer.style.height = ''
      }

      if (wasMobile !== this.isMobile) {
        this.computeMobileSnapItems()
        if (!this.isMobile) {
          this.initHotspots()
        }
      }
    })
  }

  updateVisibleProducts () {
    const mobileCards = this.querySelectorAll('.hotspot-mobile-card')
    mobileCards.forEach(card => {
      const slideIndex = parseInt(card.dataset.slideIndex)
      card.style.display = slideIndex === this.activeSlideIndex ? '' : 'none'
    })

    const productCards = this.querySelectorAll('.hotspot-product-card')
    productCards.forEach(card => {
      const slideIndex = parseInt(card.dataset.slideIndex)
      card.style.display = slideIndex === this.activeSlideIndex ? '' : 'none'
    })

    if (this.mobileScrollContainer) {
      this.mobileScrollContainer.scrollLeft = 0
    }

    if (this.scrollIndicator) {
      const visibleCards = Array.from(mobileCards).filter(
        card => parseInt(card.dataset.slideIndex) === this.activeSlideIndex
      )
      this.scrollIndicator.style.display = visibleCards.length > 2 ? '' : 'none'
      this.scrollIndicator.classList.remove('hidden')
    }
  }

  closeOtherSplitViews () {
    const allGalleries = document.querySelectorAll('image-hotspot-split')
    allGalleries.forEach(gallery => {
      if (gallery !== this) {
        const outer = gallery.querySelector('.image-hotspot-split-outer')
        if (outer && outer.classList.contains('split-view-active')) {
          outer.classList.add('closing')
          outer.classList.remove('split-view-active')
          setTimeout(() => {
            outer.style.height = ''
            outer.classList.remove('closing')
          }, 1000)
        }
      }
    })
  }

  toggleSplitView () {
    if (!this.outer) return

    if (!this.outer.classList.contains('split-view-active')) {
      const currentHeight = this.galleryContainer.offsetHeight
      this.outer.style.height = currentHeight + 'px'
      this.outer.classList.add('split-view-active')
    } else {
      this.closeSplitView()
    }
  }

  closeSplitView () {
    if (!this.outer) return

    this.outer.classList.add('closing')
    this.outer.classList.remove('split-view-active')

    setTimeout(() => {
      this.outer.style.height = ''
      this.outer.classList.remove('closing')
    }, 1000)
  }
}

customElements.define('image-hotspot-split', ImageHotspotSplit)
